import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, Calendar, CheckCircle2, AlertTriangle, Crown, DollarSign, RotateCcw, Database } from "lucide-react";
import { groupedBusinessTypes, businessTypeLabel } from "@/constants/verticals";
import { CURRENCY_CODES } from "@/lib/currencies";

function subStatus(c) {
  if (c.sold) return { key: "sold", label: "ملكية دائمة", color: "bg-amber-100 text-amber-800 border border-amber-300", days: null };
  const sub = c.subscription || {};
  const exp = sub.expires_at;
  if (!exp) return { key: "none", label: "لم يُفعّل", color: "bg-muted text-muted-foreground", days: null };
  const days = Math.floor((new Date(exp) - new Date()) / (1000*60*60*24));
  if (days < 0) return { key: "expired", label: `منتهي منذ ${-days} يوم`, color: "bg-destructive/10 text-destructive", days };
  if (days <= 7) return { key: "warn", label: `${days} يوم متبقي`, color: "bg-amber-50 text-amber-700", days };
  return { key: "ok", label: `${days} يوم متبقي`, color: "bg-emerald-50 text-emerald-700", days };
}

export default function Companies() {
  const subscriptionPlanLabels = { accounting: "نظام محاسبي فقط", ecommerce: "متجر إلكتروني فقط", accounting_ecommerce: "نظام محاسبي + متجر إلكتروني" };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", base_currency: "SYP", address: "", phone: "", vat_number: "", vat_rate: 0, subscription_plan: "accounting", business_type: "retail", enabled_currencies: [], report_currencies: ["SYP"] });
  const [allCurrencies, setAllCurrencies] = useState([]);

  const [renewFor, setRenewFor] = useState(null);
  const [renewForm, setRenewForm] = useState({ months: 1, reference: "", amount: 0, note: "" });

  const [sellFor, setSellFor] = useState(null);
  const [sellForm, setSellForm] = useState({ price: 0, currency: "USD", buyer_name: "", notes: "" });

  const load = async () => {
    try {
      const [c, cur] = await Promise.all([
        api.get("/companies"),
        api.get("/currencies").catch(() => ({ data: { rates: {} } })),
      ]);
      setItems(c.data);
      // Always offer the FULL currency catalog (not only the ones that already have a
      // configured exchange rate) so any additional currency can be enabled at company
      // setup. Merge in any custom rate codes not in the catalog. Rates are set later.
      const rateCodes = Object.keys((cur.data && cur.data.rates) || {});
      const extra = rateCodes.filter((c) => !CURRENCY_CODES.includes(c));
      setAllCurrencies([...CURRENCY_CODES, ...extra]);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name:"", base_currency:"SYP", address:"", phone:"", vat_number:"", vat_rate:0, subscription_plan:"accounting", business_type:"retail", enabled_currencies: [], report_currencies: ["SYP"] }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name:c.name, base_currency:c.base_currency, address:c.address||"", phone:c.phone||"", vat_number:c.vat_number||"", vat_rate:c.vat_rate||0, subscription_plan:c.subscription_plan || (c.public_store_enabled ? "accounting_ecommerce" : "accounting"), business_type:c.business_type||"general_trading", enabled_currencies: c.enabled_currencies || [], report_currencies: c.report_currencies?.length ? c.report_currencies : [c.base_currency || "SYP"] }); setOpen(true); };


  const save = async () => {
    try {
      const payload = { ...form, report_currencies: Array.from(new Set([form.base_currency, ...(form.report_currencies || [])])) };
      if (editing) await api.put(`/companies/${editing.id}`, payload);
      else await api.post("/companies", payload);
      toast.success("تم الحفظ"); setOpen(false); load();
    } catch(e){ toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف الشركة؟ سيتم فقد كل البيانات المرتبطة.")) return;
    try { await api.delete(`/companies/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const openRenew = (c) => { setRenewFor(c); setRenewForm({ months: 1, reference: "", amount: 0, note: "" }); };
  const doRenew = async () => {
    if (!renewForm.reference.trim()) return toast.error("رقم مرجع ShamCash مطلوب");
    try {
      const { data } = await api.post(`/companies/${renewFor.id}/renew`, { ...renewForm, amount: +renewForm.amount || 0, months: +renewForm.months || 1 });
      toast.success(`تم التجديد — ينتهي في ${new Date(data.expires_at).toLocaleDateString("ar-EG")}`);
      setRenewFor(null); load();
    } catch(e){ toast.error(formatErr(e)); }
  };

  const openSell = (c) => { setSellFor(c); setSellForm({ price: 0, currency: c.base_currency || "USD", buyer_name: c.name || "", notes: "" }); };
  const doSell = async () => {
    if (!(+sellForm.price > 0)) return toast.error("أدخل سعر البيع");
    if (!confirm(`تأكيد البيع النهائي لشركة "${sellFor.name}" مقابل ${sellForm.price} ${sellForm.currency}؟\nستصبح ملكية دائمة بدون اشتراكات.`)) return;
    try {
      await api.post(`/companies/${sellFor.id}/sell`, { ...sellForm, price: +sellForm.price });
      toast.success("تم البيع — أصبحت ملكية دائمة");
      setSellFor(null); load();
    } catch(e){ toast.error(formatErr(e)); }
  };
  const doUnsell = async (c) => {
    if (!confirm(`إلغاء بيع "${c.name}" وإرجاعها لحالة الاشتراك؟`)) return;
    try { await api.post(`/companies/${c.id}/unsell`); toast.success("تم الإلغاء"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const [backupBusy, setBackupBusy] = useState({});
  const doBackup = async (c) => {
    if (!confirm(`إنشاء نسخة احتياطية مستقلة لبيانات "${c.name}"؟\nسيتم رفعها إلى السحابة تلقائياً.`)) return;
    setBackupBusy(s => ({ ...s, [c.id]: true }));
    try {
      const { data } = await api.post(`/companies/${c.id}/backup`);
      toast.success(`تم النسخ الاحتياطي — ${(data.size_bytes/1024).toFixed(1)} KB${data.cloud_path ? " ورُفع للسحابة" : ""}`);
    } catch(e){ toast.error(formatErr(e)); }
    finally { setBackupBusy(s => ({ ...s, [c.id]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">الشركات والاشتراكات</h1>
          <p className="text-sm text-muted-foreground mt-1">إدارة الشركات + اشتراك شهري لكل شركة عبر شام كاش</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-company-button"><Plus className="w-4 h-4 me-1" /> إضافة شركة</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "تعديل شركة" : "شركة جديدة"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>الاسم</Label><Input data-testid="company-name-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
              <div>
                <Label>نوع الاشتراك</Label>
                <Select value={form.subscription_plan} onValueChange={(v)=>setForm({...form, subscription_plan:v})}>
                  <SelectTrigger data-testid="company-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accounting">نظام محاسبي فقط</SelectItem>
                    <SelectItem value="ecommerce">متجر إلكتروني فقط</SelectItem>
                    <SelectItem value="accounting_ecommerce">نظام محاسبي + متجر إلكتروني</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">يحدد هذا الاختيار وحدات النظام المشمولة، وهو مستقل عن مجال عمل الشركة.</p>
              </div>
              <div>
                <Label>نوع النشاط</Label>
                <Select value={form.business_type} onValueChange={(v)=>setForm({...form, business_type:v})}>
                  <SelectTrigger data-testid="company-btype-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {Object.entries(groupedBusinessTypes()).map(([group, items]) => (
                      <React.Fragment key={group}>
                        <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider bg-muted/50 sticky top-0">{group}</div>
                        {items.map(b => (
                          <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">تحدد الميزات المتاحة في القائمة الجانبية</p>
              </div>
              <div>
                <Label>العملة الأساسية</Label>
                <Select value={form.base_currency} onValueChange={(v)=>setForm({...form, base_currency:v})}>
                  <SelectTrigger data-testid="company-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {allCurrencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">هي العملة المرجعية للتقارير</p>
              </div>
              <div className="col-span-2">
                <Label>عملات إضافية مُتاحة (اختياري)</Label>
                <div className="p-2 border rounded max-h-48 overflow-y-auto grid grid-cols-3 gap-1 bg-muted/20">
                  {allCurrencies.filter(c => c !== form.base_currency).map(c => {
                    const on = (form.enabled_currencies || []).includes(c);
                    return (
                      <label key={c} className={`flex items-center gap-1 p-1.5 rounded cursor-pointer text-xs transition ${on ? "bg-primary/10 border border-primary/30" : "hover:bg-background border border-transparent"}`} data-testid={`cur-${c}`}>
                        <input type="checkbox" checked={on} onChange={(e) => {
                          const list = form.enabled_currencies || [];
                          setForm({ ...form, enabled_currencies: e.target.checked ? [...list, c] : list.filter(x => x !== c) });
                        }} />
                        <span className="font-mono">{c}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  <b>{(form.enabled_currencies || []).length}</b> عملة إضافية مفعّلة — سيُنشأ صندوق منفصل لكل عملة تلقائياً
                </p>
              </div>
              <div className="col-span-2">
                <Label>عملات التقارير الأساسية</Label>
                <div className="p-2 border rounded max-h-48 overflow-y-auto grid grid-cols-3 gap-1 bg-primary/5">
                  {allCurrencies.map(c => {
                    const on = (form.report_currencies || []).includes(c) || c === form.base_currency;
                    return (
                      <label key={`report-${c}`} className={`flex items-center gap-1 p-1.5 rounded cursor-pointer text-xs transition ${on ? "bg-primary/10 border border-primary/30" : "hover:bg-background border border-transparent"}`}>
                        <input type="checkbox" checked={on} disabled={c === form.base_currency} onChange={(e) => {
                          const list = form.report_currencies || [];
                          setForm({ ...form, report_currencies: e.target.checked ? [...new Set([...list, c])] : list.filter(x => x !== c) });
                        }} />
                        <span className="font-mono">{c}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">ستظهر إجماليات المبيعات والمشتريات بالعملات المحددة مع إبقاء العملة الأصلية لكل فاتورة.</p>
              </div>
              <div><Label>العنوان</Label><Input value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} /></div>
              <div><Label>الهاتف</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} /></div>
              <div>
                <Label>الرقم الضريبي <span className="text-muted-foreground text-xs font-normal">(اختياري)</span></Label>
                <Input data-testid="company-vat-input" value={form.vat_number} onChange={(e)=>setForm({...form, vat_number:e.target.value})} placeholder="اتركه فارغاً إن لم يوجد" />
                <p className="text-[10px] text-muted-foreground mt-1">عند إدخاله سيظهر تلقائياً في الفواتير المطبوعة والتقارير.</p>
              </div>
              <div>
                <Label>نسبة ضريبة القيمة المضافة % <span className="text-muted-foreground text-xs font-normal">(اختياري)</span></Label>
                <Input data-testid="company-vat-rate-input" type="number" step="0.1" min="0" max="100" value={form.vat_rate} onChange={(e)=>setForm({...form, vat_rate: e.target.value === "" ? 0 : Number(e.target.value)})} placeholder="0 = بدون ضريبة" />
                <p className="text-[10px] text-muted-foreground mt-1">اتركها 0 إن كانت شركتك غير خاضعة للضريبة. عند تحديدها تُحتسب تلقائياً على الفواتير (يمكن تجاوزها لكل فاتورة).</p>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-company-button">حفظ</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">الشركة</TableHead>
            <TableHead className="text-start">العملة</TableHead>
            <TableHead className="text-start">الاشتراك</TableHead>
            <TableHead className="text-start">ينتهي في</TableHead>
            <TableHead className="text-start">الهاتف</TableHead>
            <TableHead className="text-start">إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">لا توجد شركات</TableCell></TableRow>}
            {items.map((c) => {
              const s = subStatus(c);
              return (
                <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-2">
                      {c.name}
                      {c.sold && <Crown className="w-4 h-4 text-amber-600" title="ملكية دائمة" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{subscriptionPlanLabels[c.subscription_plan || (c.public_store_enabled ? "accounting_ecommerce" : "accounting")] || "نظام محاسبي فقط"} · {businessTypeLabel(c.business_type) || c.business_type || "—"}</p>
                  </TableCell>
                  <TableCell>{c.base_currency}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${s.color}`} data-testid={`sub-status-${c.id}`}>
                      {s.key === "sold" ? <Crown className="w-3 h-3" /> : (s.key === "expired" || s.key === "none" ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />)}
                      {s.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{c.sold ? "— لا ينتهي —" : (c.subscription?.expires_at ? new Date(c.subscription.expires_at).toLocaleDateString("ar-EG") : "—")}</TableCell>
                  <TableCell>{c.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {!c.sold && <Button size="sm" variant="outline" onClick={()=>openRenew(c)} data-testid={`renew-company-${c.id}`} className="gap-1"><RefreshCw className="w-3.5 h-3.5" /> تجديد</Button>}
                      {!c.sold && <Button size="sm" variant="outline" onClick={()=>openSell(c)} data-testid={`sell-company-${c.id}`} className="gap-1 border-amber-400 text-amber-700 hover:bg-amber-50"><DollarSign className="w-3.5 h-3.5" /> بيع</Button>}
                      {c.sold && <Button size="sm" variant="outline" onClick={()=>doUnsell(c)} data-testid={`unsell-company-${c.id}`} className="gap-1 text-muted-foreground"><RotateCcw className="w-3.5 h-3.5" /> إلغاء البيع</Button>}
                      <Button size="sm" variant="outline" onClick={()=>doBackup(c)} disabled={!!backupBusy[c.id]} data-testid={`backup-company-${c.id}`} className="gap-1 border-blue-400 text-blue-700 hover:bg-blue-50"><Database className="w-3.5 h-3.5" /> {backupBusy[c.id] ? "..." : "نسخة"}</Button>
                      <Button size="sm" variant="ghost" onClick={()=>openEdit(c)} data-testid={`edit-company-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={()=>del(c.id)} data-testid={`delete-company-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!renewFor} onOpenChange={(o)=>!o && setRenewFor(null)}>
        <DialogContent data-testid="renew-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-primary" /> تجديد اشتراك — {renewFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>عدد الأشهر</Label>
              <Select value={String(renewForm.months)} onValueChange={(v)=>setRenewForm({...renewForm, months: +v})}>
                <SelectTrigger data-testid="renew-months-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,6,12].map(n => <SelectItem key={n} value={String(n)}>{n} شهر</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>المبلغ المدفوع</Label><Input type="number" value={renewForm.amount} onChange={(e)=>setRenewForm({...renewForm, amount:e.target.value})} /></div>
            <div className="col-span-2"><Label>رقم مرجع شام كاش</Label><Input value={renewForm.reference} onChange={(e)=>setRenewForm({...renewForm, reference:e.target.value})} data-testid="renew-reference-input" dir="ltr" className="font-mono" placeholder="مثال: SC-1234567" /></div>
            <div className="col-span-2"><Label>ملاحظة</Label><Input value={renewForm.note} onChange={(e)=>setRenewForm({...renewForm, note:e.target.value})} /></div>
          </div>
          <div className="text-xs border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            سيتم تمديد الاشتراك {renewForm.months || 1} شهر من تاريخ الانتهاء الحالي (أو من اليوم إذا كان منتهياً).
          </div>
          <DialogFooter><Button onClick={doRenew} data-testid="confirm-renew-button" className="bg-emerald-600 hover:bg-emerald-700"><RefreshCw className="w-4 h-4 me-1" /> تفعيل التجديد</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sellFor} onOpenChange={(o)=>!o && setSellFor(null)}>
        <DialogContent data-testid="sell-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-600" /> بيع الشركة كملكية دائمة — {sellFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3">
            <b>تنبيه:</b> عند البيع تصبح الشركة ملكية دائمة للعميل بدون أي اشتراك أو تجديد. لن يتم فرض قيود اشتراك عليها لاحقاً.
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><Label>سعر البيع</Label><Input type="number" value={sellForm.price} onChange={(e)=>setSellForm({...sellForm, price: e.target.value})} data-testid="sell-price-input" /></div>
            <div><Label>العملة</Label>
              <Select value={sellForm.currency} onValueChange={(v)=>setSellForm({...sellForm, currency: v})}>
                <SelectTrigger data-testid="sell-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {allCurrencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>اسم المشتري</Label><Input value={sellForm.buyer_name} onChange={(e)=>setSellForm({...sellForm, buyer_name: e.target.value})} data-testid="sell-buyer-input" /></div>
            <div className="col-span-2"><Label>ملاحظات</Label><Input value={sellForm.notes} onChange={(e)=>setSellForm({...sellForm, notes: e.target.value})} data-testid="sell-notes-input" /></div>
          </div>
          <DialogFooter><Button onClick={doSell} data-testid="confirm-sell-button" className="bg-amber-600 hover:bg-amber-700"><Crown className="w-4 h-4 me-1" /> تأكيد البيع</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
